/**
 * UOM → cm canonical conversion (NFR-3).
 *
 * The FOUR canonical spellings are the ones the app itself writes —
 * `uomList` in `_services/common.service.js` and V2's `measureConverter`
 * both know exactly `meters` / `cm` / `inches` / `feet`, so those must
 * never miss. (`meters` was absent until 2026-08-19: a 5 m bay fell
 * through to the cm fallback and rendered 5 cm wide, so a 12 cm product
 * was twice the width of the whole display unit and the drag ghost
 * covered the scene — reported with `issue/simulation-load/
 * simulation-load-meters.json`.)
 *
 * The short/singular spellings below never appear in app-written data but
 * do turn up in imported master data, and they cost nothing to accept.
 * Keep this table in sync with its backend twin,
 * `Planigo/src/support/simulation-v3/uom-sup.ts`.
 *
 * An unknown unit still falls back to cm with a console warning — never
 * throw over a unit string.
 */

const CM_FACTORS: Record<string, number> = {
  cm: 1,
  centimeter: 1,
  centimeters: 1,
  centimetre: 1,
  centimetres: 1,
  mm: 0.1,
  millimeter: 0.1,
  millimeters: 0.1,
  millimetre: 0.1,
  millimetres: 0.1,
  m: 100,
  meter: 100,
  meters: 100,
  metre: 100,
  metres: 100,
  inch: 2.54,
  inches: 2.54,
  in: 2.54,
  ft: 30.48,
  feet: 30.48,
  foot: 30.48,
}

const warned = new Set<string>()

/** Multiplier that converts one unit of `uom` into centimetres. */
export const cmFactor = (uom: string | null | undefined): number => {
  const key = (uom || 'cm').trim().toLowerCase()
  const factor = CM_FACTORS[key]
  if (factor === undefined) {
    if (!warned.has(key)) {
      warned.add(key)
      // eslint-disable-next-line no-console
      console.warn(`simulationV3/units: unknown UOM "${key}" — assuming cm`)
    }
    return 1
  }
  return factor
}

/** Convert a value expressed in `uom` to centimetres. */
export const toCm = (value: number, uom: string | null | undefined): number =>
  value * cmFactor(uom)

/** Convert a centimetre value back to `uom` (round-trip for saves, NFR-3). */
export const fromCm = (
  valueCm: number,
  uom: string | null | undefined
): number => valueCm / cmFactor(uom)

/**
 * `toCm`/`fromCm` for OPTIONAL wire lengths — price-tag gap/height, peg
 * hang offsets, pegLength: the contract types them `number`, real
 * payloads omit or null them, and multiplying `undefined` yields NaN,
 * which then paints an invisible product and fails every comparison
 * silently. A non-finite input is handed back untouched so the
 * consumers' own `?? 0` fallbacks keep working.
 */
const convertOptional = (
  value: number | null | undefined,
  uom: string | null | undefined,
  factor: (uom: string | null | undefined) => number
): number | null | undefined =>
  typeof value === 'number' && Number.isFinite(value)
    ? value * factor(uom)
    : value

export const toCmOptional = (
  value: number | null | undefined,
  uom: string | null | undefined
): number => convertOptional(value, uom, cmFactor) as number

export const fromCmOptional = (
  valueCm: number | null | undefined,
  uom: string | null | undefined
): number =>
  convertOptional(valueCm, uom, (u) => 1 / cmFactor(u)) as number

